/**
 * Utilidades para manejo de inactividad y guardado de datos de formularios
 * Centraliza la lógica común de guardado antes de redirección por inactividad
 */

import { DatePipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { setData } from 'src/storage/general.actions';

/**
 * Guarda los datos de un formulario de Tenant antes de la inactividad
 * @param formValues - Valores del formulario
 * @param store - Store de NgRx
 * @param datePipe - DatePipe para formatear fechas
 */
export function saveTenantFormDataBeforeInactivity(
  formValues: any,
  store: Store,
  datePipe: DatePipe
): void {
  console.log('💾 Guardando datos del formulario de inquilino antes de inactividad');

  // Guardar datos del formulario en una key específica para restauración
  const TenantFormData = {
    FirstName: formValues.FirstName || '',
    DocumentType: formValues.DocumentType || 'C',
    DocumentNumber: formValues.DocumentNumber || '',
    Mail: formValues.Mail || ''
  };

  store.dispatch(setData({
    key: 'TenantFormData',
    value: TenantFormData,
    merge: false
  }));

  // También guardar en formato Tenant para el orquestador
  const formattedDate = datePipe.transform(new Date(), 'dd/MM/yyyy') || '';
  const Tenant = {
    ...formValues,
    PersonalDataVersion: formattedDate,
    PersonalDataLastUpdated: formattedDate,
    PersonalDataAuthorization: formValues.PersonalDataAuthorization || false
  };

  store.dispatch(setData({
    key: 'Tenant',
    value: Tenant,
    merge: true
  }));

  console.log('✅ Datos de inquilino guardados en store:', { TenantFormData, Tenant });
}

/**
 * Guarda los datos de un formulario de PolicyHolder antes de la inactividad
 * @param formValues - Valores del formulario
 * @param store - Store de NgRx
 * @param datePipe - DatePipe para formatear fechas
 */
export function savePolicyHolderFormDataBeforeInactivity(
  formValues: any,
  store: Store,
  datePipe: DatePipe
): void {
  console.log('💾 Guardando datos del formulario de propietario antes de inactividad');

  // Guardar datos del formulario en una key específica para restauración
  const PolicyHolderFormData = {
    FirstName: formValues.FirstName || '',
    LastName: formValues.LastName || '',
    Mail: formValues.Mail || '',
    CountryOfBirth: formValues.CountryOfBirth || 'Colombia',
    DocumentType: formValues.DocumentType || 'C',
    DocumentNumber: formValues.DocumentNumber || '',
    ExpeditionDate: formValues.ExpeditionDate || '',
    MobilePhone: formValues.MobilePhone || '',
    PersonalDataAuthorization: formValues.PersonalDataAuthorization || false
  };

  store.dispatch(setData({
    key: 'PolicyHolderFormData',
    value: PolicyHolderFormData,
    merge: false
  }));

  // También guardar en formato PolicyHolder para el orquestador
  const formattedDate = datePipe.transform(new Date(), 'dd/MM/yyyy') || '';
  const PolicyHolder = {
    ...formValues,
    PersonalDataVersion: formattedDate,
    PersonalDataLastUpdated: formattedDate
  };

  store.dispatch(setData({
    key: 'PolicyHolder',
    value: PolicyHolder,
    merge: true
  }));

  console.log('✅ Datos de propietario guardados en store:', { PolicyHolderFormData, PolicyHolder });
}

/**
 * Guarda los datos de un formulario de Property antes de la inactividad
 * @param formValues - Valores del formulario
 * @param store - Store de NgRx
 * @param selectedObject - Objeto de ubicación seleccionado
 */
export function savePropertyFormDataBeforeInactivity(
  formValues: any,
  store: Store,
  selectedObject: any = null
): void {
  console.log('💾 Guardando datos del formulario de inmueble antes de inactividad');

  const PropertyFormData = {
    ubicacion: formValues.ubicacion || '',
    tipoVia: formValues.tipoVia || '',
    numVia: formValues.numVia || '',
    currVia: formValues.currVia || '',
    compVia: formValues.compVia || '',
    apto: formValues.apto || '',
    barrio: formValues.barrio || '',
    resumenDireccion: formValues.resumenDireccion || '',
    resumenDireccionFinal: formValues.resumenDireccionFinal || '',
    fechaInicialContrato: formValues.fechaInicialContrato || '',
    flexCheckAutoriza: formValues.flexCheckAutoriza || false,
    selectedLocationCode: selectedObject ? selectedObject.SURACodigo__c : null
  };

  store.dispatch(setData({
    key: 'PropertyFormData',
    value: PropertyFormData,
    merge: false
  }));

  // Guardar Property en formato para el orquestador
  const Property = {
    PropertyAddress: formValues.resumenDireccionFinal || formValues.resumenDireccion || '',
    ContractStartDate: formValues.fechaInicialContrato || '',
    CityProperty: selectedObject?.SURACiudad__c || '',
    Department: selectedObject?.SURADepartamento__c || '',
    CityCode: selectedObject?.SURACodigo__c || '',
    DepartmentCode: selectedObject?.SURACodigo__c?.substring(0, 2) || '',
    Code: selectedObject?.SURACodigo__c || ''
  };

  store.dispatch(setData({
    key: 'Property',
    value: Property,
    merge: true
  }));

  console.log('✅ Datos de inmueble guardados en store:', { PropertyFormData, Property });
}
